import { useEffect, useState } from "react";
import { Loader2, LogOut, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "../lib/api";

const inputCls =
  "w-full bg-white border border-[#DCECC0] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#84CC16] focus:ring-2 focus:ring-[#84CC16]/20 transition-all";

function Login({ onSuccess }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      onSuccess(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-[#DCECC0] rounded-2xl p-8 shadow-sm" data-testid="admin-login-form">
        <p className="overline-tag">Forest Hill Realtors</p>
        <h1 className="font-serif-display text-3xl font-medium text-[#365314] mt-2 mb-8">Admin Login</h1>
        <div className="space-y-3.5">
          <input data-testid="admin-email-input" type="email" required placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          <input data-testid="admin-password-input" type="password" required placeholder="Password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
        </div>
        {error && <p className="text-sm text-red-700 mt-3" data-testid="admin-login-error">{error}</p>}
        <button data-testid="admin-login-submit-btn" type="submit" disabled={loading}
          className="w-full mt-6 bg-[#84CC16] text-[#365314] font-sub font-medium text-sm px-6 py-3.5 rounded-full hover:bg-[#9ADB3E] transition-all disabled:opacity-60 flex items-center justify-center gap-2">
          {loading && <Loader2 size={15} className="animate-spin" />} Sign In
        </button>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ phone: "", whatsapp: "", notify_email: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: leadsData }, { data: settingsData }] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/settings"),
      ]);
      setLeads(leadsData.leads);
      setSettings(settingsData);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Site settings updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="admin-dashboard">
      <header className="bg-[#FFFFFF] border-b border-[#DCECC0] px-4 sm:px-8 py-5 flex items-center justify-between">
        <div>
          <p className="font-serif-display text-xl text-[#3F6212]">Forest Hill <span className="italic text-[#84CC16]">Realtors</span></p>
          <p className="text-[11px] text-[#65A30D]">Leads Dashboard</p>
        </div>
        <div className="flex items-center gap-3">
          <a data-testid="export-csv-btn" href={`${API}/admin/leads/export`}
            className="flex items-center gap-2 text-[13px] font-sub font-bold bg-[#C2E87F] text-[#365314] px-4 py-2.5 rounded-full hover:bg-[#A9DB5E] transition-all">
            <Download size={14} /> Export CSV
          </a>
          <button data-testid="admin-logout-btn" onClick={onLogout}
            className="flex items-center gap-2 text-[13px] font-sub font-semibold text-[#3F6212] border border-[#9CCB58] px-4 py-2.5 rounded-full hover:bg-[#F4FAE8] transition-all">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-10">
        <section className="bg-white border border-[#DCECC0] rounded-2xl p-6 sm:p-8" data-testid="settings-panel">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif-display text-2xl text-[#365314]">Site Settings</h2>
          </div>
          <form onSubmit={saveSettings} className="grid sm:grid-cols-3 gap-3.5">
            <div>
              <label className="text-xs text-[#65A30D] block mb-1.5">Call Number</label>
              <input data-testid="settings-phone-input" value={settings.phone} required
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-[#65A30D] block mb-1.5">WhatsApp Number</label>
              <input data-testid="settings-whatsapp-input" value={settings.whatsapp} required
                onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-[#65A30D] block mb-1.5">Lead Notification Email</label>
              <input data-testid="settings-email-input" type="email" value={settings.notify_email} required
                onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} className={inputCls} />
            </div>
            <div className="sm:col-span-3">
              <button data-testid="settings-save-btn" type="submit" disabled={savingSettings}
                className="bg-[#84CC16] text-[#365314] text-[13px] font-sub font-medium px-6 py-3 rounded-full hover:bg-[#9ADB3E] transition-all disabled:opacity-60 flex items-center gap-2">
                {savingSettings && <Loader2 size={14} className="animate-spin" />} Save Settings
              </button>
            </div>
          </form>
        </section>

        <section data-testid="leads-panel">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif-display text-2xl text-[#365314]">Enquiries ({leads.length})</h2>
            <button data-testid="leads-refresh-btn" onClick={load}
              className="flex items-center gap-2 text-[13px] font-sub text-[#365314] border border-[#DCECC0] bg-white px-4 py-2.5 rounded-full hover:border-[#84CC16] transition-all">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-[#84CC16]" /></div>
          ) : leads.length === 0 ? (
            <p className="text-sm text-[#65A30D] bg-white border border-[#DCECC0] rounded-2xl p-10 text-center" data-testid="leads-empty">
              No enquiries yet. New leads from the website will appear here.
            </p>
          ) : (
            <div className="bg-white border border-[#DCECC0] rounded-2xl overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]" data-testid="leads-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#65A30D] border-b border-[#DCECC0]">
                    <th className="px-5 py-4">Date</th><th className="px-5 py-4">Name</th><th className="px-5 py-4">Mobile</th>
                    <th className="px-5 py-4">Interest</th><th className="px-5 py-4">Budget</th><th className="px-5 py-4">Visit Date</th>
                    <th className="px-5 py-4">Source</th><th className="px-5 py-4">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E3EFCB]">
                  {leads.map((l, i) => (
                    <tr key={i} data-testid={`lead-row-${i}`} className="hover:bg-[#F4FAE8]">
                      <td className="px-5 py-3.5 text-xs text-[#65A30D] whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString("en-IN") : "-"}</td>
                      <td className="px-5 py-3.5 font-medium text-[#365314]">{l.full_name}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap"><a href={`tel:${l.phone}`} className="text-[#365314]">{l.phone}</a></td>
                      <td className="px-5 py-3.5">{l.interest || "-"}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">{l.budget || "-"}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">{l.visit_date || "-"}</td>
                      <td className="px-5 py-3.5 text-xs text-[#65A30D]">{l.source}</td>
                      <td className="px-5 py-3.5 text-xs text-[#4D7C0F] max-w-[220px] truncate">{l.message || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#84CC16]" />
      </div>
    );
  }
  return user ? <Dashboard onLogout={logout} /> : <Login onSuccess={setUser} />;
}
